/**
 * A labeled figure with an optional delta and sparkline.
 *
 * The label is a monospace eyebrow, the figure is large and tabular, and the
 * delta always states its direction in an arrow *and* a sign — color alone
 * would fail anyone who cannot separate the mint from the rose.
 */
import type { ReactNode } from 'react'
import { ArrowDownRightIcon, ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { formatRatio, toNumber, type Decimal } from '@/lib/format'
import { cn } from '@/lib/utils'

export interface StatTileProps {
  label: string
  /** The figure. Pass a `<Money>` so formatting stays in one place. */
  value: ReactNode
  /** One quiet line under the figure — "3 accounts", "closed 2 days ago". */
  hint?: ReactNode
  /** Period-over-period change as a ratio (`0.12` → +12%). */
  delta?: Decimal
  /** What the delta is measured against, e.g. "vs last month". */
  deltaLabel?: string
  /**
   * Whether a rising number is good news. False for spending and debt, where up
   * is the wrong direction.
   */
  higherIsBetter?: boolean
  /** Values for the sparkline, oldest first. Fewer than two points draws nothing. */
  series?: (number | null)[]
  /** Top-right slot: a `CycleRing`, a badge, an icon button. */
  aside?: ReactNode
  /**
   * A block under the figure, ruled off from it. For anything `hint` cannot hold —
   * a breakdown of the figure, or the controls that decide what it counts — since
   * `hint` renders inside a `<span>` and is one quiet line by design.
   */
  footer?: ReactNode
  loading?: boolean
  className?: string
}

export function StatTile({
  label,
  value,
  hint,
  delta,
  deltaLabel,
  higherIsBetter = true,
  series,
  aside,
  footer,
  loading = false,
  className,
}: StatTileProps) {
  if (loading) {
    return (
      <div className={cn('border-hairline bg-panel rounded-lg border p-4', className)}>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-3 h-8 w-32" />
        <Skeleton className="mt-3 h-3 w-20" />
      </div>
    )
  }

  const deltaValue = toNumber(delta)
  const direction = deltaValue === null || deltaValue === 0 ? 'flat' : deltaValue > 0 ? 'up' : 'down'
  const good = direction === 'flat' ? null : (direction === 'up') === higherIsBetter
  const DeltaIcon =
    direction === 'up' ? ArrowUpRightIcon : direction === 'down' ? ArrowDownRightIcon : ArrowRightIcon

  return (
    <div className={cn('border-hairline bg-panel rounded-lg border p-4', className)}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="eyebrow">{label}</h3>
        {aside}
      </div>

      <div className="mt-2 flex items-baseline gap-2">{value}</div>

      {(deltaValue !== null || hint) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          {deltaValue !== null && (
            <span
              className={cn(
                'tabular inline-flex items-center gap-0.5 text-xs',
                good === null ? 'text-dim' : good ? 'text-mint' : 'text-rose',
              )}
            >
              <DeltaIcon className="size-3.5" aria-hidden />
              {formatRatio(Math.abs(deltaValue), { decimals: 0 })}
              <span className="sr-only">{direction === 'up' ? 'higher' : 'lower'}</span>
            </span>
          )}
          {deltaLabel && <span className="text-dim text-xs">{deltaLabel}</span>}
          {hint && <span className="text-dim text-xs">{hint}</span>}
        </div>
      )}

      {series && series.length > 1 && (
        <Sparkline series={series} className="mt-3" positive={good !== false} />
      )}

      {footer && <div className="border-hairline mt-3 border-t pt-3">{footer}</div>}
    </div>
  )
}

/**
 * A 40×12 line. Deliberately not a chart library: no axes, no tooltip, no
 * interaction — it is a shape, and Recharts inside forty tiles is forty
 * resize observers.
 */
export function Sparkline({
  series,
  className,
  positive = true,
}: {
  series: (number | null)[]
  className?: string
  positive?: boolean
}) {
  const points = series.filter((point): point is number => point !== null && Number.isFinite(point))
  if (points.length < 2) return null

  const width = 100
  const height = 24
  const min = Math.min(...points)
  const max = Math.max(...points)
  const span = max - min || 1

  const path = points
    .map((point, index) => {
      const x = (index / (points.length - 1)) * width
      const y = height - ((point - min) / span) * height
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn('h-6 w-full', className)}
      aria-hidden
    >
      <path
        d={path}
        fill="none"
        stroke={positive ? 'var(--cyan)' : 'var(--rose)'}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
